// §19.4 — shell UI: system dialogs, file pickers and permission prompts.
//
// These render in a portal **above every `.win-host`**, with a shell-owned focus trap. An app
// cannot draw there and cannot fake one — which is what makes "a user gesture is the grant"
// (§9.5) mean anything at all.
//
// The service owns the queue and the promises; the skin owns what the dialog looks like.
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { DialogRequest, VfsPath } from '@hangar/shared';

export interface DialogEntry {
  request: DialogRequest;
  /** The app that asked, for the "from <app>" line a skin may render. */
  appId: string | null;
  resolve: (value: boolean | string | null) => void;
}

/**
 * §7.7 / §9.5 — a shell-drawn file picker. Not a skin component: §6.1's list is closed and does
 * not include one, and §19.4 puts pickers in the shell's own portal precisely so an app cannot
 * draw or fake the gesture that grants it a path.
 */
export interface PickerRequest {
  id: string;
  mode: 'open' | 'save';
  title: string;
  startIn: VfsPath;
  suggestedName?: string;
  /** Lowercase, including the dot. Empty means everything. */
  extensions: string[];
  multiple: boolean;
  directory: boolean;
}

export interface PickerEntry {
  request: PickerRequest;
  /** The app that asked — the picker names it, so the user knows who is about to get access. */
  appId: string | null;
  resolve: (paths: VfsPath[] | null) => void;
}

/**
 * Shell-drawn overlays. At most one is open at a time — a launcher over a switcher over a
 * notification centre is never what anyone wants, and one slot makes `Esc` unambiguous.
 */
export const OVERLAY_IDS = [
  'launcher',
  'switcher',
  'overview',
  'notifications',
  'clipboard',
  'lock',
] as const;
export type OverlayId = (typeof OVERLAY_IDS)[number];

/** §10.3 — one candidate in the Share picker: an app whose `accepts` matched the payload. */
export interface ShareCandidate {
  appId: string;
  name: string;
  /** An `<svg>` string, so the picker looks like the launcher does. */
  icon: string;
  /** Whether it is already open — the picker says "focus" rather than "launch" (§10.3). */
  running: boolean;
}

export interface ShareRequest {
  id: string;
  /** One line describing what is being shared. Never the content itself. */
  summary: string;
  candidates: ShareCandidate[];
}

export interface ShareEntry {
  request: ShareRequest;
  /** Who is sharing. The picker names them, so the user knows who is about to hand something on. */
  appId: string | null;
  resolve: (appId: string | null) => void;
}

// ── §34.13 (M66.3) — the roster picker ───────────────────────────────────────────────────────
//
// Shell-drawn for the same reason the Share picker is (§19.4): the app that asked must not be able
// to influence who the user picks, or to learn who was offered. Explorer holds a capability that
// *opens* this and gets back an outcome — never the roster. An app that could read the roster could
// tell you who somebody knows, and §26.9 puts that behind a session for a reason.

/** One row in the picker: an accepted connection, and nothing else about them. */
export interface RosterCandidate {
  /** Their ref (D-220). The value the offer is addressed to. */
  ref: string;
  displayName: string;
  /** §26.2's derived state, so the picker can show who is actually around. Never a game id. */
  status: 'online' | 'idle' | 'busy' | 'offline';
}

export interface RosterRequest {
  id: string;
  /** One line saying what is about to be sent. The file's name, never its content. */
  summary: string;
  candidates: RosterCandidate[];
}

export interface RosterEntry {
  request: RosterRequest;
  /** Who asked. The picker names them, so the user knows who is about to send something. */
  appId: string | null;
  resolve: (ref: string | null) => void;
}

export interface ShellUiState {
  /** FIFO. Only the head is rendered; a second dialog waits its turn. */
  dialogs: DialogEntry[];
  /** §10.3 — FIFO, same rule. */
  shares: ShareEntry[];
  /** FIFO, same rule. A picker and a dialog can both be pending; the picker renders on top. */
  pickers: PickerEntry[];
  /** §34.13 — FIFO, same rule. */
  rosters: RosterEntry[];
  overlay: OverlayId | null;
  /** One-shot messages the skin's notification host renders (§6.4's stolen-chord toast). */
  toast: { id: string; message: string } | null;
}

export interface ShellUiService {
  readonly store: StoreApi<ShellUiState>;
  current(): DialogEntry | undefined;
  alert(message: string, appId?: string): Promise<void>;
  /**
   * `labels` lets the shell offer two *actions* rather than yes/no — §18 F4's dialog offers
   * Export and Empty the Recycle Bin, and both of those are things to do, not answers.
   */
  confirm(
    message: string,
    appId?: string,
    labels?: { confirmLabel?: string; cancelLabel?: string },
  ): Promise<boolean>;
  prompt(message: string, defaultValue?: string, appId?: string): Promise<string | null>;
  /** Called by the skin's Dialog when the user accepts. `value` is for `prompt`. */
  accept(id: string, value?: string): void;
  /** Called by the skin's Dialog when the user cancels or presses Escape. */
  cancel(id: string): void;

  /** §7.7 — open the shell-drawn picker. Resolves to the picked paths, or `null` if cancelled. */
  pickFiles(request: Omit<PickerRequest, 'id'>, appId?: string): Promise<VfsPath[] | null>;
  currentPicker(): PickerEntry | undefined;
  acceptPicker(id: string, paths: readonly VfsPath[]): void;
  cancelPicker(id: string): void;

  /**
   * §10.3 — the shell-drawn Share picker. Resolves to the chosen app id, or `null` when the user
   * cancels — which is what makes `share()` answer `'cancelled'` rather than failing.
   */
  pickShareTarget(request: Omit<ShareRequest, 'id'>, appId?: string): Promise<string | null>;
  currentShare(): ShareEntry | undefined;
  acceptShare(id: string, appId: string): void;
  cancelShare(id: string): void;

  /**
   * §34.13 — the shell-drawn roster picker. Resolves to the chosen peer's ref, or `null` when the
   * user cancels — which is what makes *Send to connection* answer `'cancelled'` rather than fail.
   */
  pickConnection(request: Omit<RosterRequest, 'id'>, appId?: string): Promise<string | null>;
  currentRoster(): RosterEntry | undefined;
  acceptRoster(id: string, ref: string): void;
  cancelRoster(id: string): void;

  openOverlay(id: OverlayId): void;
  closeOverlay(): void;
  toggleOverlay(id: OverlayId): void;
  activeOverlay(): OverlayId | null;

  showToast(message: string): void;
  dismissToast(): void;
}

export interface ShellUiOptions {
  newId?: () => string;
}

export function createShellUiService(options: ShellUiOptions = {}): ShellUiService {
  let counter = 0;
  const newId = options.newId ?? (() => `dlg-${++counter}`);
  const store = createStore<ShellUiState>(() => ({
    dialogs: [],
    shares: [],
    pickers: [],
    rosters: [],
    overlay: null,
    toast: null,
  }));

  const push = (
    request: Omit<DialogRequest, 'id'>,
    appId: string | undefined,
  ): Promise<boolean | string | null> =>
    new Promise((resolve) => {
      const entry: DialogEntry = {
        request: { ...request, id: newId() },
        appId: appId ?? null,
        resolve,
      };
      store.setState((s) => ({ dialogs: [...s.dialogs, entry] }));
    });

  const settleShare = (id: string, value: string | null): void => {
    const entry = store.getState().shares.find((share) => share.request.id === id);
    if (!entry) return;
    store.setState((state) => ({
      shares: state.shares.filter((share) => share.request.id !== id),
    }));
    entry.resolve(value);
  };

  const settleRoster = (id: string, value: string | null): void => {
    const entry = store.getState().rosters.find((roster) => roster.request.id === id);
    if (!entry) return;
    store.setState((state) => ({
      rosters: state.rosters.filter((roster) => roster.request.id !== id),
    }));
    entry.resolve(value);
  };

  const settlePicker = (id: string, value: VfsPath[] | null): void => {
    const entry = store.getState().pickers.find((p) => p.request.id === id);
    if (!entry) return;
    store.setState((s) => ({ pickers: s.pickers.filter((p) => p.request.id !== id) }));
    entry.resolve(value);
  };

  const settle = (id: string, value: boolean | string | null): void => {
    const entry = store.getState().dialogs.find((d) => d.request.id === id);
    if (!entry) return;
    store.setState((s) => ({ dialogs: s.dialogs.filter((d) => d.request.id !== id) }));
    entry.resolve(value);
  };

  return {
    store,

    current() {
      return store.getState().dialogs[0];
    },

    async alert(message, appId) {
      await push({ kind: 'alert', message, confirmLabel: 'OK' }, appId);
    },

    async confirm(message, appId, labels) {
      return (
        (await push(
          {
            kind: 'confirm',
            message,
            ...(labels?.confirmLabel === undefined ? {} : { confirmLabel: labels.confirmLabel }),
            ...(labels?.cancelLabel === undefined ? {} : { cancelLabel: labels.cancelLabel }),
          },
          appId,
        )) === true
      );
    },

    async prompt(message, defaultValue, appId) {
      const result = await push(
        {
          kind: 'prompt',
          message,
          ...(defaultValue !== undefined ? { defaultValue } : {}),
        },
        appId,
      );
      return typeof result === 'string' ? result : null;
    },

    accept(id, value) {
      const entry = store.getState().dialogs.find((d) => d.request.id === id);
      if (!entry) return;
      if (entry.request.kind === 'prompt') settle(id, value ?? entry.request.defaultValue ?? '');
      else settle(id, true);
    },

    cancel(id) {
      const entry = store.getState().dialogs.find((d) => d.request.id === id);
      if (!entry) return;
      settle(id, entry.request.kind === 'prompt' ? null : false);
    },

    pickFiles(request, appId) {
      return new Promise<VfsPath[] | null>((resolve) => {
        const entry: PickerEntry = {
          request: { ...request, id: newId() },
          appId: appId ?? null,
          resolve,
        };
        store.setState((s) => ({ pickers: [...s.pickers, entry] }));
      });
    },

    currentPicker() {
      return store.getState().pickers[0];
    },

    acceptPicker(id, paths) {
      settlePicker(id, [...paths]);
    },

    cancelPicker(id) {
      settlePicker(id, null);
    },

    // §10.3 — the same FIFO shape the dialog and the file picker use, for the same reason: two
    // shares at once is not a thing anyone wants, and one slot makes Escape unambiguous.
    pickShareTarget(request, appId) {
      return new Promise<string | null>((resolve) => {
        const entry: ShareEntry = {
          request: { ...request, id: newId() },
          appId: appId ?? null,
          resolve,
        };
        store.setState((state) => ({ shares: [...state.shares, entry] }));
      });
    },

    currentShare: () => store.getState().shares[0],

    acceptShare(id, appId) {
      settleShare(id, appId);
    },

    cancelShare(id) {
      settleShare(id, null);
    },

    // §34.13 — the same FIFO shape again, and the same reason: two of these at once is not a thing
    // anyone wants, and one slot makes Escape unambiguous.
    pickConnection(request, appId) {
      return new Promise<string | null>((resolve) => {
        const entry: RosterEntry = {
          request: { ...request, id: newId() },
          appId: appId ?? null,
          resolve,
        };
        store.setState((state) => ({ rosters: [...state.rosters, entry] }));
      });
    },

    currentRoster: () => store.getState().rosters[0],

    acceptRoster(id, ref) {
      settleRoster(id, ref);
    },

    cancelRoster(id) {
      settleRoster(id, null);
    },

    openOverlay(id) {
      store.setState({ overlay: id });
    },

    closeOverlay() {
      store.setState({ overlay: null });
    },

    toggleOverlay(id) {
      store.setState((s) => ({ overlay: s.overlay === id ? null : id }));
    },

    activeOverlay() {
      return store.getState().overlay;
    },

    showToast(message) {
      store.setState({ toast: { id: newId(), message } });
    },

    dismissToast() {
      store.setState({ toast: null });
    },
  };
}
